import { useWealthOverview } from '@egw/api'
import { Field, ModalDrawer, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { AccountNameForm } from '../../components'

const log = new Logger('Savings Details Page')

export interface SavingsDetailsPageProps {
  alias: number
}

export const SavingsDetailsPage: FC<SavingsDetailsPageProps> = ({ alias }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('features-account')
  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error, refetch } = useWealthOverview()

  const data = wealthOverviewData?.highYieldSavings?.accounts.find((account) => account.alias === alias)

  useEffect(() => {
    // If WealthOverview Data has returned and there are no accounts, navigate to the overview page
    if (wealthOverviewData && data === undefined) {
      log.info('No savings savings account found navigating to overview page')
      void navigate(WEALTH_ROUTES.OVERVIEW.path)
    }
  }, [data, wealthOverviewData, navigate, alias])

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {data ? (
        <>
          <PageHeader
            title={t('account_details.title')}
            subtitle={wealthOverviewData?.highYieldSavings?.productName ?? ''}
            backLinkUrl={WEALTH_ROUTES.SAVINGS_ACCOUNT_DETAILS.backPath?.replace(':alias', alias.toString())}
          />

          <div className="egw-container pt-0">
            <div className="flex flex-col gap-2 mb-8">
              <div className="border-b border-borderDark pb-3">
                <Field
                  label={t('account_details.account_type')}
                  value={wealthOverviewData?.highYieldSavings?.productName ?? ''}
                />
              </div>
              <div className="pb-3">
                <Field
                  label={t('account_details.account_name')}
                  value={data.accountName}
                  onEdit={() => {
                    setIsOpen(true)
                  }}
                />
              </div>
            </div>
          </div>
        </>
      ) : null}

      {error ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('account_savings.error')}</p>
        </div>
      ) : null}

      <ModalDrawer
        title={t('account_details.change_account_name')}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
      >
        {data ? (
          <AccountNameForm
            accountType={data.accountType}
            alias={data.alias}
            accountName={data.accountName}
            onSuccess={() => {
              void refetch()
              setIsOpen(false)
            }}
          />
        ) : null}
      </ModalDrawer>
    </>
  )
}
